import React, { useEffect, useMemo, useState } from "react";
import { Button, Box, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import styles from './practitioner.module.css';
import FormField from "../FormComponent/FormField/FormField";
import { clinicRegistration } from "../../JsonForms/ClinicRegistration";
import { axiosProxyInstance } from "../../utils/request";
import cogoToast from "cogo-toast";
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import isEqual from "lodash/isEqual"; // install lodash if needed

const validationSchema = yup.object({
    areaOfSpeciality: yup.array().min(1, 'At least one speciality is required').required('At least one speciality is required'),
});

const Speciality = ({ sectionData, details }) => {

  const [defaultValues, setDefaultValues] = useState(null);
  const [selectedValues, setSelectedValues] = useState(null);
  const currentUserId = localStorage.getItem("LPCurrentUserId");
  const ALL_SPECIALITY_OPTIONS = useMemo(() => (
    clinicRegistration.sections.find(
      (value) => value.sectionName === 'Area of Speciality'
    )?.fields[0]?.options
  ), []);

  useEffect(() => {
    if (!details?.data?.services_v2 || !details?.data?.treatments_v2  || !ALL_SPECIALITY_OPTIONS) return;

    const serviceKeys = details?.data?.services_v2 ? Object.values(details.data.services_v2) : null;
    const treatmentValues = details?.data?.treatments_v2 ? Object.values(details.data.treatments_v2) : null;

    const mappedValues = {};

    serviceKeys?.forEach(service => {
      const options = ALL_SPECIALITY_OPTIONS?.[service]?.options || [];
      const matched = options.filter(opt => treatmentValues?.includes(opt));
      if (matched.length) {
        mappedValues[service] = matched;
      }
    });
    reset({
      areaOfSpeciality: mappedValues
    })
    setSelectedValues(mappedValues);
    setDefaultValues(mappedValues);
  }, [details, ALL_SPECIALITY_OPTIONS]);

  const form = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onBlur"
  });
  
  const { register, handleSubmit, control, formState: { errors }, reset } = form;

  const allFields = sectionData.fields.flatMap(field =>
    field.fields ? field.fields : [field]
  );

  const areaOfSpecialityField = allFields.find(field => field.id === "areaOfSpeciality");

  const onSubmit = async (data) => {
    const payload = {
      btn_submit: true,
      listings: [
        {
          listing_id: details?.ID,
          title: details?.post_title,
          address_1: details?.data.address_1,
          postcode: details?.data.postcode,
          telephone_1: details?.data?.phone?.raw,
          specialization: (Object.keys(selectedValues || {})),
          treatments: Object.values(selectedValues || {}).flat(),
          provideruid: currentUserId,
          uid: currentUserId,
        }
      ]
    };
    try {
      const res = await axiosProxyInstance.post('/signup/2/', payload);
      cogoToast.success("Area of Speciality Updated Successfully!");
    } catch (err) {
      console.error("Submission failed:", err);
    }
  }

  const isDirty = !isEqual(selectedValues, defaultValues);
  return (
    <Box>
      <Typography variant="gothamMedium">
        Please select the relevant specialities, services and offered treatments for your clinic
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box className="margin-top-15">
          <Box style={{ flex: "1" }}>
              <FormField
                field={areaOfSpecialityField}
                register={register}
                selectedValues={selectedValues}
                setSelectedValues={setSelectedValues}
                control={control}
                errors={errors}
              />
            <Box className={styles.submitButtonContainer}>
              <Button
                disabled={!isDirty}
                className={styles.primaryButton}
                size="large"
                type="submit"
              >
                Save Changes
              </Button>
              <Button
                disabled={!isDirty}
                className={styles.secondaryButton}
                size="large"
                type="button"
                onClick={() => setSelectedValues(defaultValues)}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Box>
      </form>
    </Box>
  );
};

export default Speciality;
